import { toLegacyTimeZone } from '@bingads-webui-universal/time-zone-converter';
import { getCanonicalCategoriesAndColumns } from '@bingads-webui-campaign/reports-v4-column-chooser';
import { ViewItem } from '@bingads-webui-campaign/reports-v4-component';
import { uiToApiDateRange, apiToUiDateRange } from './convert-date-range';
import { uiToApiFilter, apiToUiFilter } from './convert-filter';
import { uiToApiFormats, apiToUiFormats } from './convert-conditional-formats';
import { uiToApiReportOptions, apiToUiReportOptions } from './convert-report-options';
import { convertOldTaskItem } from './convert-old-task-item';
import { getInsertionOrderSelectorValue, isInsertionOrderMonthlyView, isInsertionOrderReport, removeInsertionOrderIdFilter } from '../../helpers/insertion-order-helper';

export function toUi({
  rawTaskItem, entitySchema, permissions, history,
}) {
  const { query: { ioid } } = history;
  const taskItem = convertOldTaskItem(rawTaskItem, permissions);
  const { columns } = getCanonicalCategoriesAndColumns({
    columns: taskItem.ReportColumns,
    reportSchema: entitySchema,
    permissions,
    history,
  });
  const allFilters = entitySchema ? apiToUiFilter(taskItem, entitySchema) : {};
  const isInsertionOrderReportEnabled = isInsertionOrderReport({ reportType: taskItem.ReportType, permissions });

  return {
    reportName: taskItem.Name,
    reportType: taskItem.ReportType,
    dateRange: apiToUiDateRange(taskItem),
    filters: removeInsertionOrderIdFilter({ isInsertionOrderReportEnabled, oldFilter: allFilters }),
    sortedColumn: (taskItem.ReportSorts && taskItem.ReportSorts.length > 0) ? {
      key: taskItem.ReportSorts[0].ReportColumn,
      isSortedDescending: taskItem.ReportSorts[0].SortDirection === 'DESCENDING',
    } : {},
    conditionalFormats: taskItem.ConditionalFormats ? apiToUiFormats(taskItem.ConditionalFormats, entitySchema) : {},
    columns,
    scopeSelector: {
      AccountIds: taskItem.AccountIds,
      CampaignReportScopes: taskItem.CampaignReportScopes,
      CustomerScopes: taskItem.CustomerScopes,
      UserScopes: taskItem.UserScopes,
    },
    timeZone: taskItem.ReportTimeZone,
    schedule: {
      AttachFileToEmail: taskItem.AttachFileToEmail,
      EmailRecipients: taskItem.EmailRecipients,
      Cron: taskItem.Cron,
      StartTime: taskItem.StartTime,
      EndTime: taskItem.EndTime,
    },
    // For legacy report, disallow "Save", only allow "Save as" new report.
    isLegacyReport: !taskItem.IsPreview,
    Id: taskItem.Id,
    Type: taskItem.Type,
    ReportOptions: apiToUiReportOptions(taskItem.ReportOptions),
    ReportAggregation: taskItem.ReportAggregation,
    MaxRowCount: taskItem.MaxRowCount,
    LCID: taskItem.LCID,
    Format: taskItem.Format,
    insertionOrderViewType: isInsertionOrderMonthlyView({ reportType: taskItem.ReportType, permissions, columns }) ? ViewItem.Monthly : ViewItem.Daily,
    insertionOrderSelectorValue: getInsertionOrderSelectorValue({
      reportType: taskItem.ReportType,
      permissions,
      allFilters,
      ioidFromUrl: ioid,
    }),
  };
}


export function toApi(reportParam, reportSchema, options = { hasPagination: false, hasSort: false }) {
  const {
    dateRange,
    filters,
    scopeSelector,
    schedule,
    sortedColumn,
    insertionOrderSelectorValue,
  } = reportParam;

  const res = {
    AccountIds: scopeSelector.AccountIds,
    CampaignReportScopes: scopeSelector.CampaignReportScopes,
    CustomerScopes: scopeSelector.CustomerScopes,
    UserScopes: scopeSelector.UserScopes,
    CustomerId: reportParam.customerId,
    AttachFileToEmail: schedule.AttachFileToEmail,
    EmailRecipients: schedule.EmailRecipients,
    Cron: schedule.Cron,
    StartTime: schedule.StartTime,
    EndTime: schedule.EndTime,
    Filters: uiToApiFilter(filters, reportSchema, insertionOrderSelectorValue),
    Format: reportParam.Format || 'Xlsx',
    ClientApplicationType: reportParam.ClientApplicationType || undefined,
    LastRunReportTime: reportParam.LastRunReportTime || null,
    Id: reportParam.Id,
    IsDVS: reportParam.IsDVS || false,
    // MT use IsPreview to determine whether to go through old flow or new flow.
    // Old flow does not support Quarter column, partial week, etc. So new UI must use new flow.
    IsPreview: true,
    SchemaName: reportSchema.SchemaName,
    LCID: reportParam.LCID,
    MaxRowCount: reportParam.MaxRowCount || 0,
    Name: reportParam.reportName,
    RenderType: reportParam.RenderType || 'Table',
    ReportAggregation: reportParam.ReportAggregation || 'Summary',
    ReportColumns: reportParam.columns,
    ReportTime: uiToApiDateRange(dateRange),
    ReportTimeZone: reportParam.timeZone || 'PacificTimeUSCanadaTijuana',
    ReportOptions: uiToApiReportOptions(reportParam.ReportOptions),
    ReportType: reportParam.reportType,
    TimeZoneId: toLegacyTimeZone(reportParam.timeZone) || 71,
    Type: reportParam.Type,
    UserId: reportParam.userId,
    ConditionalFormats: reportParam.conditionalFormats ? uiToApiFormats(reportParam.conditionalFormats, reportSchema) : [],
    ReportSorts: sortedColumn.key ? [{ ReportColumn: sortedColumn.key, SortDirection: sortedColumn.isSortedDescending ? 'DESCENDING' : 'ASCENDING' }] : [],
    ...(reportParam.CollapsedRow && { CollapsedRow: reportParam.CollapsedRow }),
  };
  if (options.hasPagination) {
    res.GridPagination = reportParam.GridPagination || { RequestedPage: 1, RowsPerPage: 20 };
  }
  Object.keys(res).forEach((key) => {
    if (res[key] === null || res[key] === undefined) {
      delete res[key];
    }
  });

  return {
    ReportData: {
      '@odata.type': '#Model.ReportDataTaskItem',
      ...res,
    },
  };
}

